import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface AdminUser {
  id: string;
  email?: string;
  full_name?: string;
  telegram_id?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role: 'super_admin' | 'team_leader' | 'normal';
  access_level?: string;
  is_super_admin?: boolean;
}

interface MaintenanceStatus {
  isWarningMode: boolean;
  isMaintenanceMode: boolean;
  warningStartedAt?: string;
  maintenanceStartedAt?: string;
  maintenanceEndsAt?: string;
  maintenanceMessage?: string;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  qrLogin: (token: string) => Promise<void>;
  logout: () => void;
  isSuperAdmin: boolean;
  maintenanceStatus: MaintenanceStatus | null;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('lytspeed_admin_user');
    const sessionExpiry = localStorage.getItem('lytspeed_admin_session_expiry');

    if (storedUser && sessionExpiry) {
      const expiryDate = new Date(sessionExpiry);
      if (expiryDate > new Date()) {
        setUser(JSON.parse(storedUser));
      } else {
        localStorage.removeItem('lytspeed_admin_user');
        localStorage.removeItem('lytspeed_admin_session_expiry');
        localStorage.removeItem('lytspeed_admin_session_token');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('is_warning_mode, is_maintenance_mode, warning_started_at, maintenance_started_at, maintenance_ends_at, maintenance_message')
          .eq('id', 1)
          .maybeSingle();

        if (!error && data) {
          let finalData = { ...data };

          if (data.is_warning_mode && !data.is_maintenance_mode && data.warning_started_at) {
            const warningStart = new Date(data.warning_started_at);
            const now = new Date();
            const minutesElapsed = (now.getTime() - warningStart.getTime()) / 1000 / 60;

            if (minutesElapsed >= 5) {
              try {
                const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/maintenance-scheduler`;
                await fetch(apiUrl, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                    'Content-Type': 'application/json',
                  },
                });

                const { data: updatedData } = await supabase
                  .from('system_settings')
                  .select('is_warning_mode, is_maintenance_mode, warning_started_at, maintenance_started_at, maintenance_ends_at, maintenance_message')
                  .eq('id', 1)
                  .maybeSingle();

                if (updatedData) {
                  finalData = updatedData;
                }
              } catch (err) {
                console.error('Error triggering maintenance transition:', err);
              }
            }
          }

          setMaintenanceStatus({
            isWarningMode: finalData.is_warning_mode || false,
            isMaintenanceMode: finalData.is_maintenance_mode || false,
            warningStartedAt: finalData.warning_started_at,
            maintenanceStartedAt: finalData.maintenance_started_at,
            maintenanceEndsAt: finalData.maintenance_ends_at,
            maintenanceMessage: finalData.maintenance_message,
          });
        }
      } catch (error) {
        console.error('Error checking maintenance status:', error);
      }
    };

    checkMaintenanceStatus();
    const interval = setInterval(checkMaintenanceStatus, 15000);

    return () => clearInterval(interval);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email, password }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      setUser(data.user);
      localStorage.setItem('lytspeed_admin_user', JSON.stringify(data.user));
      localStorage.setItem('lytspeed_admin_session_token', data.session.token);
      localStorage.setItem('lytspeed_admin_session_expiry', data.session.expiresAt);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const qrLogin = async (token: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qr-login?token=${token}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'QR login failed');
      }

      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);

      setUser(data.user);
      localStorage.setItem('lytspeed_admin_user', JSON.stringify(data.user));
      localStorage.setItem('lytspeed_admin_session_token', token);
      localStorage.setItem('lytspeed_admin_session_expiry', expiresAt.toISOString());
    } catch (error) {
      console.error('QR login error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('lytspeed_admin_user');
    localStorage.removeItem('lytspeed_admin_session_token');
    localStorage.removeItem('lytspeed_admin_session_expiry');
  };

  const isSuperAdmin = user?.role === 'super_admin' || user?.is_super_admin === true;

  return (
    <AdminAuthContext.Provider value={{ user, loading, login, qrLogin, logout, isSuperAdmin, maintenanceStatus }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
